// Utils
import classnames from 'classnames'
import { useTranslation } from 'next-i18next'

import { FormattedPrice } from '@src/blocks/currency'
import { STATSIG_EXPERIMENTS_BUCKETS } from '@src/config/statsig-experiments.config'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { useNewPdpDesignV2Bucket } from '@src/quince-site-pdp/src/templates/productPage/hooks/useShowNewPdpDesignV2'

import { RichText } from '../../molecules'
import Column from '../column/Column'
import Swiper from '../swiper'
import { SwiperProps } from '../swiper/Swiper'

import { TransparentPricingProps } from './transparent-pricing.types'
import styles from './transparentPricing.module.scss'

const COMMON_SLIDER_SETTINGS: SwiperProps = {
  children: [],
  // @i18n-ignore
  id: 'transparent-pricing-slider',
  numberOfVisibleSlides: 2,
  showNavigator: false,
}

const MOBILE_SLIDER_SETTINGS = {
  ...COMMON_SLIDER_SETTINGS,
  numberOfVisibleSlides: 1,
  dots: true,
}

const DESKTOP_SLIDER_SETTINGS = {
  ...COMMON_SLIDER_SETTINGS,
}

const TransparentPricingLegacy = ({ items = [], ...props }: TransparentPricingProps) => {
  const deviceInfo = useDeviceInfo()
  const { isB2BMode } = useStorefrontModeContext()
  const { t } = useTranslation('common')
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const totalPrice = (items || []).reduce((total, curr) => total + curr.value, 0)
  const settings = isMobile ? MOBILE_SLIDER_SETTINGS : DESKTOP_SLIDER_SETTINGS

  const newPdpDesignV2Bucket = useNewPdpDesignV2Bucket()
  const showNewPdpDesignV2 = newPdpDesignV2Bucket !== STATSIG_EXPERIMENTS_BUCKETS.CONTROL
  const classes = classnames(styles.container, props.className, {
    [styles.container_newPdpDesignV2 ?? '']: showNewPdpDesignV2,
    [styles.container_b2bMode ?? '']: isB2BMode,
  })

  return (
    <div className={classes}>
      <div className={styles.container__text}>
        <div className={styles.container__text__header}>
          {props.headerRichTextProps ? (
            <RichText {...props.headerRichTextProps} />
          ) : (
            (props.header ?? t('transparent_pricing'))
          )}
        </div>
        <div className={styles.container__text__body}>
          {props.bodyRichTextProps ? (
            <RichText {...props.bodyRichTextProps} />
          ) : (
            (props.body ?? t('transparent_pricing_body'))
          )}
        </div>
      </div>
      <div
        className={classnames(styles.container__right, showNewPdpDesignV2 && styles.container__right_newPdpDesignV2)}
      >
        <Swiper
          {...settings}
          className={classnames(
            styles.container__slider,
            showNewPdpDesignV2 && styles.container__slider_newPdpDesignV2,
          )}
        >
          <table
            className={classnames(
              styles.container__right__data,
              showNewPdpDesignV2 && styles.container__right__data_newPdpDesignV2,
            )}
          >
            <tbody>
              {items.map((item, key) => {
                return (
                  <tr
                    className={classnames(
                      styles.container__right__data__row,
                      showNewPdpDesignV2 && styles.container__right__data__row_newPdpDesignV2,
                    )}
                    key={key}
                  >
                    <th className={styles.container__right__data__row__text}>{item.name}</th>
                    <td className={styles.container__right__data__row__price}>
                      <FormattedPrice amount={item.value} />
                    </td>
                  </tr>
                )
              })}
              {items.length > 0 && (
                <tr
                  className={classnames(
                    styles.container__right__data__row,
                    styles.container__right__data__total,
                    showNewPdpDesignV2 && styles.container__right__data__total_newPdpDesignV2,
                  )}
                >
                  <th className={styles.container__right__data__total__text}>{t('total_cost')}</th>
                  <td className={styles.container__right__data__total__text}>
                    <FormattedPrice amount={totalPrice} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className={styles.container__right__columns}>
            {props.traditionalPrice ? (
              <div className={styles.container__right__columns__body}>
                <Column
                  // @i18n-ignore
                  header={<FormattedPrice amount={props.quincePrice} />}
                  className={styles.container__right__columns__column}
                  smallerUpper={props.traditionalPrice > props.quincePrice}
                  internalText={t('total_cost')}
                />
                <Column
                  // @i18n-ignore
                  header={<FormattedPrice amount={props.traditionalPrice} />}
                  footer={t('traditional_retailer')}
                  showInfoTooltip
                  internalText={t('total_cost')}
                  infoTooltipText={t('traditional_retailer_disclaimer')}
                />
              </div>
            ) : null}
          </div>
        </Swiper>
      </div>
    </div>
  )
}

export default TransparentPricingLegacy
